import React, { useState, useEffect, useMemo } from 'react';
import { Catalyst, CatalystFavorite, ImpactLevel, OutcomeStatus } from '../types';
import { apiService } from '../services/api';
import { Star, StarOff, Search, Calendar, AlertCircle, CheckCircle, XCircle, Clock, Heart, ExternalLink } from 'lucide-react';

interface CatalystCalendarProps {
  isSuperuser?: boolean;
}

const CatalystCalendar: React.FC<CatalystCalendarProps> = ({ isSuperuser = false }) => {
  const [catalysts, setCatalysts] = useState<Catalyst[]>([]);
  const [favorites, setFavorites] = useState<CatalystFavorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState<OutcomeStatus | 'All'>('All');
  const [selectedImpact, setSelectedImpact] = useState<ImpactLevel | 'All'>('All');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [scrapeText, setScrapeText] = useState('');
  const [scrapeInstructions, setScrapeInstructions] = useState('');
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [isUserSuperuser, setIsUserSuperuser] = useState(isSuperuser);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catalystsData, favoritesData, user] = await Promise.all([
        apiService.fetchCatalysts(),
        apiService.fetchCatalystFavorites(),
        apiService.getCurrentUser()
      ]);
      setCatalysts(catalystsData);
      setFavorites(favoritesData);
      setIsUserSuperuser(Boolean(user?.is_superuser));
    } catch (error) {
      console.error('Error loading catalyst data:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleScrape = async () => {
    if (!scrapeText && !scrapeInstructions) {
      return;
    }
    try {
      setScrapeLoading(true);
      await apiService.scrapeCatalysts({
        rawText: scrapeText,
        userInstructions: scrapeInstructions
      });
      setScrapeText('');
      setScrapeInstructions('');
      await loadData();
    } catch (error) {
      console.error('Error scraping catalysts:', error);
    } finally {
      setScrapeLoading(false);
    }
  };

  const favoriteValues = useMemo(() => {
    const assetFavorites = favorites.filter(f => f.favorite_type === 'asset').map(f => f.favorite_value);
    const mechanismFavorites = favorites.filter(f => f.favorite_type === 'mechanism').map(f => f.favorite_value);
    const targetFavorites = favorites.filter(f => f.favorite_type === 'target').map(f => f.favorite_value);
    return { assetFavorites, mechanismFavorites, targetFavorites };
  }, [favorites]);

  const isCatalystFavorited = (catalyst: Catalyst): boolean => {
    return (
      favoriteValues.assetFavorites.includes(catalyst.asset) ||
      (catalyst.moa && favoriteValues.mechanismFavorites.includes(catalyst.moa)) ||
      (catalyst.target && favoriteValues.targetFavorites.includes(catalyst.target))
    );
  };

  const toggleFavorite = async (catalyst: Catalyst, type: 'asset' | 'mechanism' | 'target', value: string) => {
    try {
      const existingFavorite = favorites.find(
        f => f.favorite_type === type && f.favorite_value === value
      );
      
      if (existingFavorite) {
        await apiService.deleteCatalystFavorite(existingFavorite.id);
        setFavorites(prev => prev.filter(f => f.id !== existingFavorite.id));
      } else {
        const newFavorite = await apiService.createCatalystFavorite(type, value);
        setFavorites(prev => [...prev, newFavorite]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const filteredCatalysts = useMemo(() => {
    return catalysts.filter(catalyst => {
      const matchesSearch = !searchTerm || 
        catalyst.asset.toLowerCase().includes(searchTerm.toLowerCase()) ||
        catalyst.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (catalyst.target && catalyst.target.toLowerCase().includes(searchTerm.toLowerCase())) ||
        catalyst.event_description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesOutcome = selectedOutcome === 'All' || catalyst.outcome === selectedOutcome;
      const matchesImpact = selectedImpact === 'All' || catalyst.impact === selectedImpact;
      const matchesFavorites = !showFavoritesOnly || isCatalystFavorited(catalyst);
      
      return matchesSearch && matchesOutcome && matchesImpact && matchesFavorites;
    });
  }, [catalysts, searchTerm, selectedOutcome, selectedImpact, showFavoritesOnly, favoriteValues]);

  const getOutcomeIcon = (outcome: OutcomeStatus) => {
    switch (outcome) {
      case 'Positive': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'Negative': return <XCircle className="w-4 h-4 text-rose-500" />;
      case 'Delayed': return <Clock className="w-4 h-4 text-orange-500" />;
      case 'Neutral': return <AlertCircle className="w-4 h-4 text-slate-400" />;
      default: return <Clock className="w-4 h-4 text-indigo-400" />;
    }
  };

  const getImpactColor = (impact: ImpactLevel) => {
    switch (impact) {
      case 'High': return 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20';
      case 'Moderate': return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
      case 'Low': return 'bg-slate-50 text-slate-600 ring-1 ring-slate-400/20';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <div className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-3">
              <Calendar className="w-8 h-8 text-indigo-600" />
              Catalyst Calendar
            </h1>
            <p className="text-sm text-slate-500 mt-2">Track clinical trial readouts, FDA decisions, and key events</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search assets, companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 w-full text-sm bg-slate-50 border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:bg-white outline-none transition-all"
              />
            </div>
            
            <select
              value={selectedOutcome}
              onChange={(e) => setSelectedOutcome(e.target.value as OutcomeStatus | 'All')}
              className="px-4 py-2.5 text-sm bg-slate-50 border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:bg-white outline-none transition-all"
            >
              <option value="All">All Outcomes</option>
              <option value="Pending">Pending</option>
              <option value="Positive">Positive</option>
              <option value="Negative">Negative</option>
              <option value="Neutral">Neutral</option>
              <option value="Delayed">Delayed</option>
            </select>
            
            <select
              value={selectedImpact}
              onChange={(e) => setSelectedImpact(e.target.value as ImpactLevel | 'All')}
              className="px-4 py-2.5 text-sm bg-slate-50 border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:bg-white outline-none transition-all"
            >
              <option value="All">All Impact Levels</option>
              <option value="High">High</option>
              <option value="Moderate">Moderate</option>
              <option value="Low">Low</option>
            </select>
            
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-all flex items-center justify-center gap-2 ${
                showFavoritesOnly
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                  : 'bg-slate-50 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'
              }`}
            >
              <Heart className={`w-4 h-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
              Favorites Only
            </button>
          </div>
        </div>

        {/* Superuser Scrape Panel */}
        {isUserSuperuser && (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Scrape Catalysts</h3>
                <p className="text-xs text-slate-500">Superuser-only ingestion for catalyst events.</p>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                Superuser
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Raw Text</label>
                <textarea
                  value={scrapeText}
                  onChange={(e) => setScrapeText(e.target.value)}
                  placeholder="Paste catalyst data here..."
                  className="mt-2 w-full h-28 p-3 text-sm bg-slate-50 border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:bg-white outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Instructions (optional)</label>
                <textarea
                  value={scrapeInstructions}
                  onChange={(e) => setScrapeInstructions(e.target.value)}
                  placeholder="e.g., only include KRAS G12C catalysts"
                  className="mt-2 w-full h-28 p-3 text-sm bg-slate-50 border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:bg-white outline-none transition-all"
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={handleScrape}
                disabled={scrapeLoading || (!scrapeText && !scrapeInstructions)}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-xl transition-all shadow-lg shadow-indigo-200"
              >
                {scrapeLoading ? 'Running...' : 'Run Scrape'}
              </button>
            </div>
          </div>
        )}

        {/* Catalyst List */}
        <div className="space-y-4">
          {filteredCatalysts.length === 0 ? (
            <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-12 text-center">
              <p className="text-slate-500">No catalysts found matching your filters.</p>
            </div>
          ) : (
            filteredCatalysts.map((catalyst) => {
              const isFavorited = isCatalystFavorited(catalyst);
              const assetLink = catalyst.asset_id ? `/emerging-assets/${catalyst.asset_id}` : null;
              return (
                <div
                  key={catalyst.id}
                  className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Left: Date and Status */}
                    <div className="flex-shrink-0 lg:w-48">
                      <div className="flex items-center gap-3 mb-2">
                        {getOutcomeIcon(catalyst.outcome)}
                        <span className="text-xs font-semibold text-slate-500 uppercase">
                          {catalyst.outcome}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-slate-900">{catalyst.event_date}</div>
                      {catalyst.conference && (
                        <div className="text-xs text-slate-500 mt-1">{catalyst.conference}</div>
                      )}
                    </div>

                    {/* Center: Main Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900 mb-1">{catalyst.asset}</h3>
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <span className="font-medium">{catalyst.company}</span>
                            {catalyst.ticker && (
                              <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono">
                                {catalyst.ticker}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getImpactColor(catalyst.impact)}`}>
                          {catalyst.impact} Impact
                        </span>
                      </div>
                      {assetLink && (
                        <div className="mb-3">
                          <a
                            href={assetLink}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                          >
                            View Emerging Asset <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      )}

                      <p className="text-sm text-slate-700 mb-4 leading-relaxed">
                        {catalyst.event_description}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {catalyst.target && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Target:</span>
                            <span className="text-xs font-medium text-slate-700">{catalyst.target}</span>
                            <button
                              onClick={() => toggleFavorite(catalyst, 'target', catalyst.target!)}
                              className="text-slate-400 hover:text-rose-500 transition-colors"
                            >
                              {favoriteValues.targetFavorites.includes(catalyst.target) ? (
                                <Star className="w-3.5 h-3.5 fill-current" />
                              ) : (
                                <StarOff className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        )}
                        {catalyst.moa && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Mechanism:</span>
                            <span className="text-xs font-medium text-slate-700">{catalyst.moa}</span>
                            <button
                              onClick={() => toggleFavorite(catalyst, 'mechanism', catalyst.moa!)}
                              className="text-slate-400 hover:text-rose-500 transition-colors"
                            >
                              {favoriteValues.mechanismFavorites.includes(catalyst.moa) ? (
                                <Star className="w-3.5 h-3.5 fill-current" />
                              ) : (
                                <StarOff className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Asset:</span>
                          <span className="text-xs font-medium text-slate-700">{catalyst.asset}</span>
                          <button
                            onClick={() => toggleFavorite(catalyst, 'asset', catalyst.asset)}
                            className="text-slate-400 hover:text-rose-500 transition-colors"
                          >
                            {favoriteValues.assetFavorites.includes(catalyst.asset) ? (
                              <Star className="w-3.5 h-3.5 fill-current" />
                            ) : (
                              <StarOff className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {catalyst.indications && catalyst.indications.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {catalyst.indications.map((ind, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium"
                            >
                              {ind}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalystCalendar;
